import { ActionButton, Stack, Text } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { useFetch } from '@moko/ui';
import {
  getPricingParameters,
  resolvePricingParameters,
  type IPricingParameter,
  type IResolvedPricingParameter,
} from '../../services/pricingParameterService';
import PricingParameterDrawer from './PricingParameterDrawer';
import PricingParameterEffectiveCards from './PricingParameterEffectiveCards';
import PricingParameterHistory from './PricingParameterHistory';
import PricingParameterLoadError from './PricingParameterLoadError';
import PricingParameterMaintenance from './PricingParameterMaintenance';
import { toEffectiveRows } from './pricingParameterHelpers';
import type { PricingParameterCatalog } from './usePricingParameterCatalog';

export interface TenantPricingParameterPanelProps {
  tenantId: number;
  isAdmin: boolean;
  catalog: PricingParameterCatalog;
  pricingItemId: number;
  pricingDate: string;
}

const TenantPricingParameterPanel = ({
  tenantId,
  isAdmin,
  catalog,
  pricingItemId,
  pricingDate,
}: TenantPricingParameterPanelProps) => {
  const [historyOpen, { setTrue: openHistory, setFalse: closeHistory }] =
    useBoolean(false);
  const resolvedFetch = useFetch<IResolvedPricingParameter[]>({
    queryKey: [
      'resolvedPricingParameters',
      tenantId,
      pricingItemId,
      pricingDate,
    ],
    queryFn: (abortController) =>
      resolvePricingParameters(abortController, {
        pricingItemId,
        pricingDate,
      }),
  });
  const tenantFetch = useFetch<IPricingParameter[]>({
    queryKey: ['tenantPricingParameters', tenantId, pricingItemId],
    queryFn: (abortController) =>
      getPricingParameters(abortController, 'tenant', pricingItemId),
  });

  const refreshAll = () => {
    resolvedFetch.refetch();
    tenantFetch.refetch();
  };

  const loading = resolvedFetch.loading || tenantFetch.loading;
  const loadError = resolvedFetch.error ?? tenantFetch.error;
  const hasData = !loading && !loadError;
  const tenantRecords = hasData ? tenantFetch.data ?? [] : [];
  const effectiveRows =
    hasData && resolvedFetch.data
      ? toEffectiveRows(catalog.definitions, resolvedFetch.data)
      : [];

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      <PricingParameterLoadError error={loadError} />
      <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
        <Text variant="mediumPlus">Effective values</Text>
        <ActionButton
          iconProps={{ iconName: 'History' }}
          text="History"
          onClick={openHistory}
        />
      </Stack>
      <PricingParameterEffectiveCards rows={effectiveRows} loading={loading} />
      <Text variant="mediumPlus">Tenant overrides</Text>
      <PricingParameterMaintenance
        scope="tenant"
        canMutate={isAdmin && catalog.ready && hasData}
        definitions={catalog.definitions}
        pricingItemId={pricingItemId}
        records={tenantRecords}
        loading={loading}
        emptyTitle="No tenant overrides"
        emptySubtitle="No override exists for this tenant and pricing item."
        onChanged={refreshAll}
      />
      <PricingParameterDrawer
        open={historyOpen}
        title="History of tenant and global values"
        size="large"
        onClose={closeHistory}
      >
        <PricingParameterHistory
          definitions={catalog.definitions}
          pricingItemId={pricingItemId}
          tenantRecords={tenantRecords}
        />
      </PricingParameterDrawer>
    </Stack>
  );
};

export default TenantPricingParameterPanel;
